// Labels and colors from the approved design (reference/ui.jsx). Rule text reflects the active thresholds.
import { fmtMoney } from './format.js';
import type { RecKey, SpendStatus, Thresholds, ZipStatus } from './types.js';

export const REC: Record<RecKey, { label: string; hex: string }> = {
  invest: { label: 'Invest More', hex: '#2291D0' },
  maintain: { label: 'Maintain', hex: '#2E9E5A' },
  watch: { label: 'Watch', hex: '#D99A1B' },
  reduce: { label: 'Reduce', hex: '#D13B3B' },
  new: { label: 'New / Developing', hex: '#F1BA44' },
  expansion: { label: 'Expansion Candidate', hex: '#7AB0DD' },
  unrated: { label: 'Unavailable', hex: '#9AA4B8' },
  inactive: { label: 'Inactive', hex: '#C5CBD6' }
};

export function recRule(rec: RecKey, t?: Thresholds): string {
  const g = fmtMoney(t ? t.investMinGap : 25000);
  switch (rec) {
    case 'invest': return `Established active ZIP below target with at least ${g} remaining opportunity and valid data. Positive growth is not required; a Large Decline Alert on a valid comparison moves it to Watch. No minimum revenue or OOH.`;
    case 'maintain': return `At or above target, or below target with less than ${g} remaining opportunity and not Watch or Reduce.`;
    case 'watch': return 'Below target and a valid comparable period triggers the Large Decline Alert. Investigate before increasing investment. Never from provisional YoY.';
    case 'reduce': return `Below target, with revenue and share both declining in ${t ? t.reduceConsecutive : 2} consecutive valid comparable periods and at least one Large Decline Alert. One weak period never triggers Reduce.`;
    case 'new': return 'Added in the 2026 service-area version. Shown with its metrics; not judged until it has a full comparable period in service.';
    case 'expansion': return 'Flag for leadership review: outside the service area, valid opportunity denominator, borders a current service ZIP. No minimum revenue or OOH. Not a recommendation to enter.';
    case 'unrated': return 'Denominator invalid or missing. Share and recommendation stay unavailable until it is resolved.';
    case 'inactive': return 'Removed from the service area.';
  }
}

export const REC_ORDER: RecKey[] = ['invest', 'maintain', 'watch', 'reduce', 'new', 'expansion', 'unrated'];

export const STATUS: Record<ZipStatus, string> = {
  established: 'Established', new: 'New / Developing', removed: 'Inactive / Removed', expansion: 'Expansion Candidate', outside: 'Outside service area', external: 'Outside modeled universe'
};
export const STATUS_SHORT: Record<ZipStatus, string> = { established: 'Established', new: 'New', removed: 'Removed', expansion: 'Expansion', outside: 'Outside', external: 'Outside universe' };

export const SPEND: Record<SpendStatus, [string, string]> = {
  verified: ['Verified', 'var(--ce-success)'], unverified: ['Unverified', 'var(--ce-warning)'], missing: ['Missing', 'var(--ce-danger)'], na: ['Not applicable', 'var(--ce-navy-300)']
};
